import axios from "axios";
import { BASE_URL } from "./api";
import { resolveTenantId } from "../utils/tenant";

const getAuthHeaders = () => {
  const company = JSON.parse(localStorage.getItem("company") || "{}");
  const directToken = localStorage.getItem("token");
  const token = company.token || directToken;
  const tenantId = resolveTenantId() || company.slug || "";

  if (!token) {
    throw new Error("Not authenticated");
  }

  return {
    Authorization: `Bearer ${token}`,
    "X-Tenant-ID": tenantId,
    "X-Company-Slug": tenantId,
  };
};

const getCompanyId = () => {
  const company = JSON.parse(localStorage.getItem("company") || "{}");
  return company._id || company.id || null;
};

// Icon mapping for dynamic icon resolution
export const ICON_MAP = {
  BsPeople: "BsPeople",
  BsFileEarmarkText: "BsFileEarmarkText",
  BsPersonBadge: "BsPersonBadge",
  BsUiChecksGrid: "BsUiChecksGrid",
  BsGear: "BsGear",
  BsClipboardCheck: "BsClipboardCheck",
  BsPlug: "BsPlug",
  BsGraphUp: "BsGraphUp",
  BsSignpost2: "BsSignpost2",
  BsCalendarEvent: "BsCalendarEvent",
  BsShieldCheck: "BsShieldCheck",
  BsCalendarCheck: "BsCalendarCheck",
  BsClockHistory: "BsClockHistory",
  BsCashStack: "BsCashStack",
  BsCreditCard: "BsCreditCard",
  BsCreditCard2Back: "BsCreditCard2Back",
  BsBoxArrowRight: "BsBoxArrowRight",
  BsBank: "BsBank",
  BsReceipt: "BsReceipt",
  BsChatDots: "BsChatDots",
  BsMortarboard: "BsMortarboard",
  BsPhone: "BsPhone",
  BsBarChart: "BsBarChart",
  BsRocketTakeoff: "BsRocketTakeoff",
  BsStickies: "BsStickies",
};

/**
 * Get sidebar modules from backend
 */
export const getSidebarModules = async () => {
  try {
    const headers = getAuthHeaders();

    console.log(
      "[Sidebar Service] Fetching modules from:",
      `${BASE_URL}/api/sidebar/modules`,
    );

    const response = await axios.get(`${BASE_URL}/api/sidebar/modules`, {
      headers,
    });

    console.log(
      "[Sidebar Service] Modules fetched successfully:",
      response.data.data?.length || 0,
    );
    return response.data.data;
  } catch (error) {
    console.error(
      "[Sidebar Service] Error fetching sidebar modules:",
      error.message,
    );
    throw error;
  }
};

/**
 * Get ALL sidebar modules with full details (Admin only)
 * Returns complete module data including isActive, _id, order, permissions
 * Used for admin management/settings UI
 */
export const getAllSidebarModules = async () => {
  try {
    const headers = getAuthHeaders();

    const response = await axios.get(
      `${BASE_URL}/api/sidebar/modules/admin/all`,
      {
        headers,
      },
    );

    console.log(
      "[Sidebar Service] Admin modules fetched:",
      response.data.data?.length || 0,
    );
    return response.data.data;
  } catch (error) {
    console.error(
      "[Sidebar Service] Error fetching admin sidebar modules:",
      error.message,
    );
    throw error;
  }
};

/**
 * Update sidebar modules (Admin only)
 */
export const updateSidebarModules = async (modules, companyId = null) => {
  try {
    const headers = getAuthHeaders();

    const response = await axios.post(
      `${BASE_URL}/api/sidebar/modules`,
      {
        modules,
        companyId: companyId || getCompanyId(),
      },
      {
        headers,
      },
    );

    return response.data;
  } catch (error) {
    console.error("Error updating sidebar modules:", error);
    throw error;
  }
};

/**
 * Delete a sidebar module (Admin only)
 */
export const deleteSidebarModule = async (moduleId) => {
  try {
    const headers = getAuthHeaders();

    const response = await axios.delete(
      `${BASE_URL}/api/sidebar/modules/${moduleId}`,
      {
        headers,
      },
    );

    return response.data;
  } catch (error) {
    console.error("Error deleting sidebar module:", error);
    throw error;
  }
};

/**
 * Get sidebar overrides for current tenant (Admin only)
 */
export const getSidebarOverrides = async () => {
  try {
    const headers = getAuthHeaders();

    const response = await axios.get(`${BASE_URL}/api/sidebar/overrides`, {
      headers,
    });

    return response.data.data;
  } catch (error) {
    console.error("Error fetching sidebar overrides:", error);
    throw error;
  }
};

/**
 * Update sidebar overrides for tenant (Admin only)
 */
export const updateSidebarOverrides = async (companyId, overrides) => {
  try {
    const headers = getAuthHeaders();

    const response = await axios.put(
      `${BASE_URL}/api/sidebar/overrides`,
      {
        companyId: companyId || getCompanyId(),
        overrides,
      },
      {
        headers,
      },
    );

    return response.data;
  } catch (error) {
    console.error("Error updating sidebar overrides:", error);
    throw error;
  }
};

/**
 * Delete sidebar overrides for tenant (Admin only)
 */
export const deleteSidebarOverrides = async () => {
  try {
    const headers = getAuthHeaders();

    const response = await axios.delete(`${BASE_URL}/api/sidebar/overrides`, {
      headers,
    });

    return response.data;
  } catch (error) {
    console.error("Error deleting sidebar overrides:", error);
    throw error;
  }
};

/**
 * Get role → permissions mapping for the sidebar RBAC UI (Admin only)
 * Returns each role's permissions array and badge color.
 */
export const getSidebarRoles = async () => {
  try {
    const headers = getAuthHeaders();

    const response = await axios.get(`${BASE_URL}/api/sidebar/roles`, {
      headers,
    });

    return response.data.data.roles;
  } catch (error) {
    console.error("Error fetching sidebar role permissions:", error);
    throw error;
  }
};
